/**
 * AuthScreen Component
 * Login / Sign-up screen shown when user is not authenticated
 */

import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Turnstile } from '@marsidev/react-turnstile';
import GoogleSignInButton from '../../components/GoogleSignInButton';
import './AuthScreen.css';

export const AuthScreen = ({
  // Form state
  showSignup,
  setShowSignup,
  loginMode,
  setLoginMode,
  email,
  setEmail,
  password,
  setPassword,
  confirmPassword,
  setConfirmPassword,
  fullName,
  setFullName,
  showPassword,
  setShowPassword,
  accessError,
  setAccessError,

  // Handlers
  handleSignup,
  handleAuth,
  handleDemoCtaClick,
  setCurrentPage,

  // Google Sign-In success/error
  setCurrentUser,
  setIsAuthenticated,
  setIsFirstLogin,

  // Signup success modal
  showSignupSuccessModal,
  setShowSignupSuccessModal,
}) => {
  const navigate = useNavigate();
  const [isGoogleSigningIn, setIsGoogleSigningIn] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);

  return (
    <div className="App">
      <div className="container auth-page-container">
        <div className="auth-wrapper">
          <div className="auth-back-row">
            <button
              type="button"
              className="auth-back-button"
              onClick={() => navigate('/')}
            >
              <span className="auth-back-icon">←</span>
              Back to home
            </button>
          </div>
          <div className="auth-container">
            <div className="auth-left">
              <div className="auth-glow"></div>
              <div className="auth-left-content">
                <div className="auth-logo-card">
                  <div className="auth-logo-mark">
                    <svg width="96" height="48" viewBox="0 0 100 40" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet">
                      <g transform="translate(8.5 0)">
                        <path d="M10 30 L20 10 L30 30 Z" stroke="#ffffff" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                        <line x1="38" y1="20" x2="38" y2="20" stroke="#ffffff" strokeWidth="3" strokeLinecap="round"/>
                        <line x1="43" y1="15" x2="43" y2="25" stroke="#ffffff" strokeWidth="3" strokeLinecap="round"/>
                        <line x1="48" y1="10" x2="48" y2="30" stroke="#ffffff" strokeWidth="3" strokeLinecap="round"/>
                        <line x1="53" y1="12" x2="53" y2="28" stroke="#ffffff" strokeWidth="3" strokeLinecap="round"/>
                        <line x1="58" y1="18" x2="58" y2="22" stroke="#ffffff" strokeWidth="3" strokeLinecap="round"/>
                        <line x1="63" y1="14" x2="63" y2="26" stroke="#ffffff" strokeWidth="3" strokeLinecap="round"/>
                        <line x1="68" y1="16" x2="68" y2="24" stroke="#ffffff" strokeWidth="3" strokeLinecap="round"/>
                        <line x1="73" y1="20" x2="73" y2="20" stroke="#ffffff" strokeWidth="3" strokeLinecap="round"/>
                      </g>
                    </svg>
                  </div>
                  <div>
                    <p className="auth-logo-eyebrow">Algodentra</p>
                    <h3 className="auth-logo-title">Clinical copilot</h3>
                    <p className="auth-logo-copy">Encrypted, AI-assisted documentation built for modern practices.</p>
                  </div>
                </div>

                <h2 className="auth-left-title">Get Your Time Back.</h2>
                <p className="auth-left-subtitle">
                  Keep your team synced with a login that mirrors the landing page: clean, calm, and secure for both dentists and patients.
                </p>

                <div className="auth-pill-row">
                  <span className="auth-pill">HIPAA/PIPEDA-ready</span>
                  <span className="auth-pill">Zero raw PHI in cloud</span>
                  <span className="auth-pill">Dentist & Patient portals</span>
                  <span className="auth-pill">Built-in noise removal</span>
                </div>

                <div className="auth-metric-grid">
                  <div className="auth-metric">
                    <span className="metric-value">10x</span>
                    <span className="metric-label">Faster SOAP prep</span>
                  </div>
                  <div className="auth-metric">
                    <span className="metric-value">24/7</span>
                    <span className="metric-label">Secure portal access</span>
                  </div>
                  <div className="auth-metric">
                    <span className="metric-value">100%</span>
                    <span className="metric-label">Local PII redaction</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="auth-right">
              <div className="auth-card">
                <div className="auth-tabs">
                  <button
                    type="button"
                    className={`auth-tab ${!showSignup ? 'is-active' : ''}`}
                    onClick={() => {
                      setShowSignup(false);
                      setAccessError('');
                      setConfirmPassword('');
                      setTurnstileToken(null);
                      turnstileRef.current?.reset();
                    }}
                  >
                    Sign in
                  </button>
                  <button
                    type="button"
                    className={`auth-tab ${showSignup ? 'is-active' : ''}`}
                    onClick={() => {
                      setShowSignup(true);
                      setAccessError('');
                      setPassword('');
                      setTurnstileToken(null);
                      turnstileRef.current?.reset();
                    }}
                  >
                    Create account
                  </button>
                </div>

                <div className="auth-header">
                  <p className="auth-label">
                    {loginMode === 'doctor' ? 'Dentist workspace' : 'Patient portal'}
                  </p>
                  <h2 className="auth-title">
                    {showSignup ? 'Join Algodentra' : 'Welcome back'}
                  </h2>
                  <p className="auth-subtitle">
                    {showSignup
                      ? `Create a secure ${loginMode === 'doctor' ? 'dentist' : 'patient'} account to sync notes and billing.`
                      : `Login as ${loginMode === 'doctor' ? 'Dentist' : 'Patient'} and keep your documentation flowing securely.`
                    }
                  </p>
                </div>

                <div className="auth-mode-toggle">
                  <button
                    type="button"
                    className={`mode-chip ${loginMode === 'doctor' ? 'is-active' : ''}`}
                    onClick={() => {
                      setLoginMode('doctor');
                      setAccessError('');
                      setTurnstileToken(null);
                      turnstileRef.current?.reset();
                    }}
                  >
                    Dentist
                  </button>
                  <button
                    type="button"
                    className={`mode-chip ${loginMode === 'patient' ? 'is-active' : ''}`}
                    onClick={() => {
                      setLoginMode('patient');
                      setAccessError('');
                      setTurnstileToken(null);
                      turnstileRef.current?.reset();
                    }}
                  >
                    Patient
                  </button>
                </div>

                <form className="auth-form" onSubmit={showSignup ? handleSignup : handleAuth} style={{ display: isGoogleSigningIn ? 'none' : 'block' }}>
                  {showSignup && (loginMode === 'doctor' || loginMode === 'patient') && (
                    <div className="form-group">
                      <label htmlFor="fullName">Full Name</label>
                      <input
                        type="text"
                        id="fullName"
                        value={fullName}
                        onChange={(e) => {
                          setFullName(e.target.value);
                          setAccessError('');
                        }}
                        placeholder="Enter your full name"
                        required
                        style={{
                          borderColor: accessError ? '#e53e3e' : undefined,
                          cursor: 'pointer'
                        }}
                      />
                    </div>
                  )}

                  {(loginMode === 'doctor' || loginMode === 'patient') && (
                    <>
                      <div className="form-group">
                        <label htmlFor="email">Email</label>
                        <input
                          type="email"
                          id="email"
                          value={email}
                          onChange={(e) => {
                            setEmail(e.target.value);
                            setAccessError('');
                          }}
                          placeholder="Enter your email"
                          required
                          style={{
                            borderColor: accessError ? '#e53e3e' : undefined,
                            cursor: 'pointer'
                          }}
                        />
                      </div>
                      <div className="form-group" style={{ marginTop: '0.5rem' }}>
                        <label htmlFor="password">Password</label>
                        <div className="form-input-wrapper">
                          <input
                            type={showPassword ? "text" : "password"}
                            id="password"
                            value={password}
                            onChange={(e) => {
                              setPassword(e.target.value);
                              setAccessError('');
                            }}
                            placeholder="Enter your password"
                            required
                            style={{
                              borderColor: accessError ? '#e53e3e' : undefined,
                              cursor: 'pointer'
                            }}
                          />
                          <button
                            type="button"
                            className={`input-visibility-toggle ${showPassword ? 'is-active' : ''}`}
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? (
                              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M3 3L21 21" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M10.584 10.588C10.2088 10.9645 9.99771 11.4701 9.99999 12C10.0023 12.5299 10.2179 13.0338 10.5963 13.4101C10.9747 13.7865 11.4796 14 12.0095 14C12.5394 14 13.0443 13.7864 13.4227 13.4099" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M14.1213 14.1213L9.87868 9.87868" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M7.36168 7.36395C8.77957 6.43509 10.3731 5.95236 12 5.95236C15.8182 5.95236 19.2045 8.2134 20.9545 12C20.4375 13.1325 19.7818 14.1273 19.0409 14.9627" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M6.47697 6.47528L5.99997 6.00028C4.17033 7.52599 2.92318 9.42845 2.5 12C3.13636 15.9545 6.59091 18.0473 12 18.0473C12.9227 18.0473 13.8136 17.9409 14.6682 17.7346" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            ) : (
                              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1.5 12C3.5 7 7.5 4 12 4C16.5 4 20.5 7 22.5 12C20.5 17 16.5 20 12 20C7.5 20 3.5 17 1.5 12Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </button>
                        </div>
                        {!showSignup && (
                          <div className="auth-helper-links">
                            <a href="/forgot-password">Forgot password?</a>
                          </div>
                        )}
                      </div>

                      {showSignup && (
                        <>
                          <div className="form-group">
                            <label htmlFor="confirmPassword">Confirm Password</label>
                            <div className="form-input-wrapper">
                              <input
                                type={showPassword ? "text" : "password"}
                                id="confirmPassword"
                                value={confirmPassword}
                                onChange={(e) => {
                                  setConfirmPassword(e.target.value);
                                  setAccessError('');
                                }}
                                placeholder="Confirm your password"
                                required
                                style={{
                                  borderColor: accessError ? '#e53e3e' : undefined,
                                  cursor: 'pointer'
                                }}
                              />
                              <button
                                type="button"
                                className={`input-visibility-toggle ${showPassword ? 'is-active' : ''}`}
                                aria-label={showPassword ? 'Hide passwords' : 'Show passwords'}
                                onClick={() => setShowPassword(!showPassword)}
                              >
                                {showPassword ? (
                                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3 3L21 21" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M10.584 10.588C10.2088 10.9645 9.99771 11.4701 9.99999 12C10.0023 12.5299 10.2179 13.0338 10.5963 13.4101C10.9747 13.7865 11.4796 14 12.0095 14C12.5394 14 13.0443 13.7864 13.4227 13.4099" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M14.1213 14.1213L9.87868 9.87868" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M7.36168 7.36395C8.77957 6.43509 10.3731 5.95236 12 5.95236C15.8182 5.95236 19.2045 8.2134 20.9545 12C20.4375 13.1325 19.7818 14.1273 19.0409 14.9627" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M6.47697 6.47528L5.99997 6.00028C4.17033 7.52599 2.92318 9.42845 2.5 12C3.13636 15.9545 6.59091 18.0473 12 18.0473C12.9227 18.0473 13.8136 17.9409 14.6682 17.7346" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                ) : (
                                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1.5 12C3.5 7 7.5 4 12 4C16.5 4 20.5 7 22.5 12C20.5 17 16.5 20 12 20C7.5 20 3.5 17 1.5 12Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                )}
                              </button>
                            </div>
                          </div>

                          <div className="password-hints">
                            <p className="password-hints-title">Password requirements:</p>
                            <ul className="password-hints-list">
                              <li className={password.length >= 5 ? 'is-valid' : ''}>
                                {password.length >= 5 ? '✓' : '○'} At least 5 characters
                              </li>
                              <li className={/[A-Z]/.test(password) ? 'is-valid' : ''}>
                                {/[A-Z]/.test(password) ? '✓' : '○'} At least one capital letter
                              </li>
                              <li className={/[0-9]/.test(password) ? 'is-valid' : ''}>
                                {/[0-9]/.test(password) ? '✓' : '○'} At least one number
                              </li>
                              <li className={password && confirmPassword && password === confirmPassword ? 'is-valid' : ''}>
                                {password && confirmPassword && password === confirmPassword ? '✓' : '○'} Passwords match
                              </li>
                            </ul>
                          </div>
                        </>
                      )}
                    </>
                  )}

                  {accessError && (
                    <p className="auth-error">
                      {accessError}
                    </p>
                  )}

                  <button type="submit" className="auth-button" disabled={!turnstileToken}>
                    {showSignup ? 'Sign up' : 'Sign in'}
                  </button>
                </form>

                {(loginMode === 'doctor' || loginMode === 'patient') && (
                  <div style={{ marginTop: '20px' }}>
                    {!isGoogleSigningIn && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        margin: '20px 0',
                        gap: '10px'
                      }}>
                        <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }}></div>
                        <span style={{ color: '#64748b', fontSize: '14px' }}>Or continue with</span>
                        <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }}></div>
                      </div>
                    )}

                    <GoogleSignInButton
                      loginMode={loginMode}
                      onSuccess={(data) => {
                        localStorage.setItem('accessToken', data.accessToken);
                        localStorage.setItem('refreshToken', data.refreshToken);
                        localStorage.setItem('user', JSON.stringify(data.user));
                        localStorage.setItem('loginMode', loginMode);
                        setCurrentUser(data.user);
                        setIsFirstLogin(data.isFirstLogin === true);
                        if (loginMode === 'doctor') {
                          setCurrentPage('main');
                          setIsAuthenticated(true);
                        } else if (loginMode === 'patient') {
                          setCurrentPage('patient-portal');
                          setIsAuthenticated(true);
                        }
                      }}
                      onError={(error) => {
                        setAccessError(error);
                        setIsGoogleSigningIn(false);
                      }}
                      onLoadingChange={setIsGoogleSigningIn}
                    />

                    {!isGoogleSigningIn && (
                      <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center' }}>
                        <Turnstile
                          ref={turnstileRef}
                          siteKey={process.env.REACT_APP_CF_TURNSTILE_SITE_KEY}
                          onSuccess={(token) => setTurnstileToken(token)}
                          onExpire={() => setTurnstileToken(null)}
                          onError={() => setTurnstileToken(null)}
                          options={{ theme: 'dark' }}
                        />
                      </div>
                    )}
                  </div>
                )}

                {(loginMode === 'doctor' || loginMode === 'patient') && (
                  <div className="auth-toggle" style={{ display: isGoogleSigningIn ? 'none' : 'block' }}>
                    <p>
                      {showSignup ? 'Already have an account?' : "New here?"}
                      {' '}
                      <button
                        type="button"
                        onClick={() => {
                          setShowSignup(!showSignup);
                          setAccessError('');
                          setPassword('');
                          setConfirmPassword('');
                          setFullName('');
                        }}
                      >
                        {showSignup ? 'Sign in' : 'Create account'}
                      </button>
                    </p>
                  </div>
                )}

                <div className="auth-legal-links">
                  <button
                    onClick={() => setCurrentPage('privacy')}
                    className="legal-link"
                  >
                    Privacy Policy
                  </button>
                  <span className="legal-separator">•</span>
                  <button
                    onClick={() => setCurrentPage('terms')}
                    className="legal-link"
                  >
                    Terms & Conditions
                  </button>
                </div>
              </div>
            </div>
          </div>

        </div>
        {/* CTA Section */}
        <div className="cta-section">
          <div className="cta-shell">
            <div className="cta-grid">
              <div className="cta-left">
                <div className="cta-badge">Live product demo</div>
                <h2 className="cta-title">Ready to transform your practice?</h2>
                <p className="cta-description">
                  In 10 minutes, we'll show you how Algodentra shrinks SOAP creation, CDA codes, and patient summaries into one secure flow tailored to dentists.
                </p>
                <div className="cta-list">
                  <div className="cta-list-item">
                    <span className="cta-dot" />
                    <span>Walk through chairside recording to AI-assisted notes</span>
                  </div>
                  <div className="cta-list-item">
                    <span className="cta-dot" />
                    <span>Security, PII redaction, and role-based access baked in</span>
                  </div>
                  <div className="cta-list-item">
                    <span className="cta-dot" />
                    <span>Implementation plan for your clinic in under a week</span>
                  </div>
                </div>
                <div className="cta-actions">
                  <button
                    className="cta-button"
                    onClick={handleDemoCtaClick}
                  >
                    <span className="cta-btn-icon">
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                        <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    </span>
                    <span>Book a 10 min demo</span>
                  </button>
                  <p className="cta-note">No credit card • We'll send a recap + recording</p>
                </div>
              </div>
              <div className="cta-right">
                <div className="cta-logo-card">
                  <svg className="cta-logo-hero" viewBox="0 0 100 40" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet">
                    <g transform="translate(8.5 0)">
                      <path d="M10 30 L20 10 L30 30 Z" stroke="#ffffff" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                      <line x1="38" y1="20" x2="38" y2="20" stroke="#ffffff" strokeWidth="3" strokeLinecap="round"/>
                      <line x1="43" y1="15" x2="43" y2="25" stroke="#ffffff" strokeWidth="3" strokeLinecap="round"/>
                      <line x1="48" y1="10" x2="48" y2="30" stroke="#ffffff" strokeWidth="3" strokeLinecap="round"/>
                      <line x1="53" y1="12" x2="53" y2="28" stroke="#ffffff" strokeWidth="3" strokeLinecap="round"/>
                      <line x1="58" y1="18" x2="58" y2="22" stroke="#ffffff" strokeWidth="3" strokeLinecap="round"/>
                      <line x1="63" y1="14" x2="63" y2="26" stroke="#ffffff" strokeWidth="3" strokeLinecap="round"/>
                      <line x1="68" y1="16" x2="68" y2="24" stroke="#ffffff" strokeWidth="3" strokeLinecap="round"/>
                      <line x1="73" y1="20" x2="73" y2="20" stroke="#ffffff" strokeWidth="3" strokeLinecap="round"/>
                    </g>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Signup Success Modal */}
      {showSignupSuccessModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
              <div style={{
                width: '48px', height: '48px', borderRadius: '50%',
                backgroundColor: '#d4edda', display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12L11 14L15 10" stroke="#48bb78" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="12" r="10" stroke="#48bb78" strokeWidth="2"/>
                </svg>
              </div>
              <h2 style={{ margin: 0, color: '#2d3748', fontSize: '1.5rem', fontWeight: '700' }}>
                Account Created!
              </h2>
            </div>

            <p style={{ color: '#4a5568', fontSize: '1rem', lineHeight: '1.6', marginBottom: '1rem' }}>
              Account created successfully! Please check your email to verify your account before logging in.
            </p>

            <div style={{
              backgroundColor: '#e6f7ff', border: '1px solid #91d5ff',
              borderRadius: '8px', padding: '1rem', marginBottom: '2rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0, marginTop: '2px' }}>
                  <path d="M3 8L10.89 13.26C11.21 13.47 11.58 13.58 12 13.58C12.42 13.58 12.79 13.47 13.11 13.26L21 8M5 19H19C20.1 19 21 18.1 21 17V7C21 5.9 20.1 5 19 5H5C3.9 5 3 5.9 3 7V17C3 18.1 3.9 19 5 19Z" stroke="#1890ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div>
                  <p style={{ margin: 0, fontSize: '0.9rem', color: '#0050b3', fontWeight: '600', marginBottom: '0.25rem' }}>
                    Check your inbox
                  </p>
                  <p style={{ margin: 0, fontSize: '0.85rem', color: '#096dd9', lineHeight: '1.4' }}>
                    We've sent a verification link to your email. Click the link to verify your account and start using Algodentra.
                  </p>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowSignupSuccessModal(false)}
                style={{
                  padding: '0.75rem 2rem',
                  backgroundColor: '#48bb78',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#38a169'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#48bb78'}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
